import type { FC, ReactNode } from 'react';
import { Box, Skeleton } from '@mui/material';

const BODY_ROWS = 8;
const HEADER_HEIGHT = 56;
const ROW_HEIGHT = 52;
// Same widths as the columns of XCreativesTable: preview, name, account, created, flow, type, use in automation, status, priority.
const COLUMN_TEMPLATE = '80px minmax(320px, 1fr) 260px 120px 320px 100px 160px 90px 130px';
const NAME_WIDTHS = ['82%', '68%', '90%', '74%', '86%', '62%', '78%', '70%'];

const Cell: FC<{ children: ReactNode; center?: boolean }> = ({ children, center }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: center ? 'center' : 'flex-start', px: 1 }}>
    {children}
  </Box>
);

const SkeletonRow: FC<{ index: number }> = ({ index }) => {
  const isHeader = index === 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: COLUMN_TEMPLATE,
        height: isHeader ? HEADER_HEIGHT : ROW_HEIGHT,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Cell center>
        {isHeader ? (
          <Skeleton width={44} />
        ) : (
          <Skeleton variant="rounded" width={40} height={40} />
        )}
      </Cell>
      <Cell>
        <Skeleton width={isHeader ? 60 : NAME_WIDTHS[(index - 1) % NAME_WIDTHS.length]} />
      </Cell>
      <Cell>
        <Skeleton width={isHeader ? 56 : '80%'} />
      </Cell>
      <Cell>
        <Skeleton width={isHeader ? 56 : 80} />
      </Cell>
      <Cell>
        <Skeleton width={isHeader ? 40 : '70%'} />
      </Cell>
      <Cell>
        <Skeleton width={isHeader ? 36 : 48} />
      </Cell>
      <Cell center>
        {isHeader ? <Skeleton width={96} /> : <Skeleton variant="rounded" width={34} height={20} />}
      </Cell>
      <Cell center>
        {isHeader ? <Skeleton width={40} /> : <Skeleton variant="circular" width={20} height={20} />}
      </Cell>
      <Cell>
        {isHeader ? <Skeleton width={52} /> : <Skeleton variant="rounded" width="100%" height={32} />}
      </Cell>
    </Box>
  );
};

const XCreativesGridSkeleton: FC = () => (
  <Box
    role="status"
    aria-busy="true"
    aria-label="X creatives are loading"
    sx={{
      flex: 1,
      minHeight: 400,
      width: '100%',
      overflow: 'hidden',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
    }}
  >
    {Array.from({ length: BODY_ROWS + 1 }, (_, index) => (
      <SkeletonRow key={index} index={index} />
    ))}
  </Box>
);

export default XCreativesGridSkeleton;
